<script setup lang="ts">
import {
  getFlagStatus,
  toFlagKey,
  type FlagTypes,
} from "@/helpers/shared/flags";
import Button from "@/stories/buttons/Button/Button.vue";
import BigDialog from "@/stories/dialogs/BigDialog.vue";
import DialogContent from "@/stories/dialogs/DialogContent.vue";
import DialogHeader from "@/stories/dialogs/DialogHeader.vue";
import CheckboxV2 from "@/stories/form/CheckboxV2/CheckboxV2.vue";
import type { ActiveStatus } from "@/types";
import { ref } from "vue";

defineEmits<{ close: [] }>();

type Flag = {
  key: FlagTypes;
  title: string;
  description: string;
};

const FLAGS: Flag[] = [
  {
    key: "newPlanner",
    title: "New Planner",
    description: "Enables the new planner. Navigate to the planner to test it.",
  },
  {
    key: "matomoLogs",
    title: "Matomo Logs",
    description: "Enables Matomo event logging in the browser console.",
  },
  {
    key: "domCounter",
    title: "DOM Counter",
    description: "Enables the DOM counter component.",
  },
  {
    key: "performanceLogs",
    title: "Performance Logs",
    description: "Enables performance logs in the browser console",
  },
];

const flags = ref<Record<FlagTypes, ActiveStatus>>({
  newPlanner: getFlagStatus("newPlanner"),
  matomoLogs: getFlagStatus("matomoLogs"),
  domCounter: getFlagStatus("domCounter"),
  performanceLogs: getFlagStatus("performanceLogs"),
});

const updateFlag = (key: FlagTypes, value: boolean) => {
  const status: ActiveStatus = value ? "on" : "off";
  flags.value[key] = status;
};

const handleApply = () => {
  Object.keys(flags.value).forEach((key) => {
    const type = key as FlagTypes;
    localStorage.setItem(toFlagKey(type), flags.value[type]);
  });

  window.location.reload();
};
</script>

<template>
  <BigDialog @close="$emit('close')">
    <DialogHeader @close="$emit('close')">Developer Tools</DialogHeader>
    <DialogContent>
      <div class="flex flex-col gap-4 mb-6">
        <div
          v-for="flag in FLAGS"
          :key="flag.key"
          class="border border-neutral300 rounded-md px-4 py-3"
        >
          <CheckboxV2
            :modelValue="flags[flag.key] === 'on'"
            :label="flag.title"
            :description="flag.description"
            @update:modelValue="(value) => updateFlag(flag.key, value)"
          />
        </div>
      </div>
      <Button text="Apply" @click="handleApply" />
    </DialogContent>
  </BigDialog>
</template>
